<template>
    <v-container class="px-8 px-md-3 roommates-container">

        <!-- Hero Header -->
        <div class="roommates-hero">
            <h1 class="roommates-hero__title">Find a Roommate</h1>
            <p class="roommates-hero__subtitle">
                Before you start looking for housing, let's find you a roommate
                <span class="roommates-hero__accent">(or 2 or 3)</span>
            </p>
        </div>

        <!-- My Roommates Section -->
        <div class="my-roommates-section">
            <div class="my-roommates-section__header">
                <h2 class="my-roommates-section__title">My Roommates</h2>
                <span class="my-roommates-section__count" v-if="userRoommates && userRoommates.length > 0">
                    {{ userRoommates.length }}
                </span>
            </div>
            <p class="my-roommates-section__description">
                People you've connected with and agreed to room together
            </p>

            <v-row class="d-flex mb-4" v-if="userRoommates && userRoommates.length > 0">
                <v-col
                    cols="12" md="6" lg="4" xl="3"
                    v-for="(userRoommate, n) in userRoommates"
                    :key="n"
                    class="roommate-list"
                >
                    <RoommateCardMini :roommate="userRoommate" />
                </v-col>
            </v-row>

            <div class="my-roommates-section__empty" v-else>
                <v-icon color="grey lighten-1" size="32">mdi-account-group-outline</v-icon>
                <span>No roommates yet</span>
            </div>

            <div class="my-roommates-section__actions">
                <AddRoommateDialog v-if="sentRequests.length < maxRequestsSent"/>
                <span class="my-roommates-section__remaining">
                    {{ maxRequestsSent - sentRequests.length }} request{{ maxRequestsSent - sentRequests.length !== 1 ? 's' : '' }} remaining
                </span>
            </div>
        </div>

        <div class="tabs-header-row">
            <v-tabs
                    class="roommates-tabs"
                    slider-size="3"
                    :grow="$vuetify.breakpoint.smAndDown"
                    :centered="$vuetify.breakpoint.smAndDown"
                    height="60"
            >
                <v-tab>Matches</v-tab>
                <v-tab :disabled="!isProfileCompleted">Favorites</v-tab>
                <v-tab :disabled="!isProfileCompleted">Contacted</v-tab>
                <v-tab :disabled="!isProfileCompleted" class="roommate-requests-tab">
                    Roommate Requests
                    <span v-if="roommateRequests.length > 0" class="request-count-badge">
                        {{ roommateRequests.length }}
                    </span>
                </v-tab>

            <v-tab-item class="mt-4">
                <div v-if="isProfileCompleted && matchedRoommates.length > 0">

                    <v-row class="justify-start">
                        <v-col cols="12" md="6" lg="4" xl="3" v-for="(matchedRoommate, n) in matchedRoommates" :key="n">
                            <RoommateCard :roommate="matchedRoommate" />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col class="d-flex py-10 justify-center">
                            <Paginate state="Roommates" collection="matchedRoommates" list="getMatchedRoommates"/>
                        </v-col>
                    </v-row>
                </div>
                <v-row v-else-if="isProfileCompleted">
                    <v-col cols="12">
                        <p>No roommates match your preferences.</p>
                    </v-col>
                </v-row>
                <v-row class="justify-center" v-else>
                    <v-col cols="12" md="6" xl="3">
                        <v-card class="mx-auto my-4">
                            <v-card-title>What's next: Complete your profile</v-card-title>
                            <v-card-text>
                                <p>Before you can start seeing matches, you must fill out your roommate profile</p>

                                <v-btn color="primary" class="mt-4" @click="$router.push('/roommate-profile')">Go to roommate profile</v-btn>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-tab-item>

            <v-tab-item class="mt-4" v-if="isProfileCompleted">
                <v-row class="justify-start">
                    <v-col cols="12" md="6" lg="4" xl="3" v-for="(favoriteRoommate, n) in favoriteRoommates" :key="n">
                        <RoommateCard :roommate="favoriteRoommate" />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="d-flex py-10 justify-center">
                        <Paginate state="Roommates" collection="favoriteRoommates" list="getFavoriteRoommates"/>
                    </v-col>
                </v-row>
            </v-tab-item>

            <v-tab-item class="mt-4" v-if="isProfileCompleted">
                <v-row class="justify-start">
                    <v-col cols="12" md="6" lg="4" xl="3" v-for="(contactedRoommate, n) in contactedRoommates" :key="n">
                        <RoommateCard :roommate="contactedRoommate" />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="d-flex py-10 justify-center">
                        <Paginate state="Roommates" collection="contactedRoommates" list="getContactedRoommates"/>
                    </v-col>
                </v-row>
            </v-tab-item>

            <v-tab-item class="mt-4" v-if="isProfileCompleted">
                <RoommateRequestDialog :sentRequests="sentRequests" :receivedRequests="receivedRequests" />
            </v-tab-item>
            </v-tabs>

            <div class="sort-filter-wrapper" v-if="isProfileCompleted && !$vuetify.breakpoint.smAndDown">
                <RoommateSortByFilter />
            </div>
        </div>

        <div class="d-flex justify-center">
            <div class="col-md-6 mb-4">
                <FaqCollapsible :page="page"/>
            </div>
        </div>
    </v-container>
</template>

<script>
    import RoommateCard from "../components/RoommateCard";
    import RoommateRequestDialog from "../components/RoommateRequestDialog";
    import RoommateCardMini from '../components/RoommateCardMini';
    import Paginate from "../components/Paginate";
    import RoommateSortByFilter from "../components/RoommateSortByFilter";
    import FaqCollapsible from "@/components/FaqCollapsible";
    import SiteService from "@/services/site.service";
    import AddRoommateDialog from "@/components/AddRoommateDialog"

    export default {
        name: 'Roommates',
        components: {
			FaqCollapsible,
            Paginate,
            RoommateCard,
            RoommateSortByFilter,
            RoommateCardMini,
            RoommateRequestDialog,
            AddRoommateDialog
        },
        data() {
            return {
                page: null,
                sentRequests: [],
                receivedRequests: [],
                maxRequestsSent: 6
            }
        },
        computed: {
            userRoommates() {
                return this.$store.getters.getUserRoommates;
            },
            roommateRequests() {
                return this.$store.getters.getRoommateRequests;
            },
            matchedRoommates () {
                return this.$store.getters.getMatchedRoommates;
            },
            favoriteRoommates () {
                return this.$store.getters.getFavoriteRoommates;
            },
            contactedRoommates () {
                return this.$store.getters.getContactedRoommates;
            },
            user () {
                return this.$store.getters.getUser;
            },
            isProfileCompleted() {
                return this.user.needs_roommate
                    && this.user.ideal_roommate_gender
                    && this.user.ideal_roommate_age_young
                    && this.user.ideal_roommate_age_old
            }
        },
        methods: {
            sortRoommateRequests() {
                this.sentRequests = [];
                this.receivedRequests = [];

                if (this.roommateRequests && this.roommateRequests.length > 0) {
                    const vm = this
                    this.roommateRequests.forEach(request => {
                        if (request.sender.id === vm.user.id) {
                            this.sentRequests.push(request);
                        } else {
                            this.receivedRequests.push(request);
                        }
                    });
                }
            },
            async getRoommateUpdates() {
                await this.$store.dispatch('getUserRoommates');
                await this.$store.dispatch('getRoommateRequests');
                await this.$store.dispatch('getMatchedRoommates');
                await this.$store.dispatch('getFavoriteRoommates');
                await this.$store.dispatch('getContactedRoommates');
            }
        },
        async beforeMount() {
            this.page = await SiteService.getRoommatesPage();
            await this.getRoommateUpdates()
            this.sortRoommateRequests()
        },
    }
</script>
<style>

    /* Hero Header */
    .roommates-hero {
        margin-top: 40px;
        margin-bottom: 32px;
    }
    .roommates-hero__title {
        font-size: 36px;
        line-height: 1.2;
        font-weight: 800;
        color: #1a1a2e;
        margin-bottom: 8px;
        letter-spacing: -0.5px;
    }
    .roommates-hero__subtitle {
        font-size: 18px;
        line-height: 1.5;
        font-weight: 400;
        color: #64748b;
        margin: 0;
    }
    .roommates-hero__accent {
        color: #2d76e3;
        font-weight: 500;
    }

    /* My Roommates Section */
    .my-roommates-section {
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        border-radius: 16px;
        padding: 24px;
        margin-bottom: 32px;
        border: 1px solid #e2e8f0;
    }
    .my-roommates-section__header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 6px;
    }
    .my-roommates-section__title {
        font-size: 18px;
        font-weight: 700;
        color: #1a1a2e;
        margin: 0;
    }
    .my-roommates-section__count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 24px;
        height: 24px;
        padding: 0 8px;
        font-size: 12px;
        font-weight: 600;
        color: #2d76e3;
        background: rgba(45, 118, 227, 0.1);
        border-radius: 12px;
    }
    .my-roommates-section__description {
        font-size: 14px;
        color: #64748b;
        margin: 0 0 20px 0;
        font-style: normal;
    }
    .my-roommates-section__empty {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 20px;
        background: rgba(255, 255, 255, 0.7);
        border-radius: 10px;
        margin-bottom: 16px;
        color: #94a3b8;
        font-size: 15px;
    }
    .my-roommates-section__actions {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
    }
    .my-roommates-section__remaining {
        font-size: 13px;
        color: #94a3b8;
        font-weight: 500;
    }

    .v-slide-group__next, .v-slide-group__prev {
        all: unset !important;
    }

    .roommates-tabs .v-slide-group__wrapper {
        border-bottom: 1px solid #d8d8d8 !important;
    }
    .roommates-tabs .v-tab {
        text-transform: unset;
        letter-spacing: 0;
        font-size: 16px;
        font-weight: 400;
        color: #000 !important;
    }
    .roommates-tabs .v-tab--active {
        color: #000;
    }
    .roommates-tabs .results-found {
        font-size: 14px !important;
    }
    .roommates-tabs .next-complete {
        border-radius: 10px !important;
    }
    .roommates-tabs .next-complete .v-card__title {
        font-size: 20px !important;
        font-weight: 700;
    }
    .roommates-tabs .next-complete .v-card__text p {
        font-size: 16px !important;
        font-weight: 400;
        color: #000;
    }
    .roommate-list .modal-close-btn {
        top: 5px;
        right: 5px;
    }
    .roommate-list .roommate-name {
        font-size: 16px !important;
        font-weight: 700;
        color: #000;
    }
    .roommate-list .roommate-info {
        font-size: 14px !important;
        font-weight: 400;
        color: #000;
    }
    .roommate-list .v-card {
        border-radius: 10px !important;
    }
    .v-autocomplete__content .v-list-item p {
        font-size: 14px !important;
    }
    .add-roommate-autocomplete.btn-edu-height .v-input__slot .v-input__append-inner {
        margin-top: 0 !important;
    }

    /* Tabs header layout */
    .tabs-header-row {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        position: relative;
    }
    .tabs-header-row .roommates-tabs {
        flex: 1;
    }
    .sort-filter-wrapper {
        position: absolute;
        right: 0;
        top: 10px;
        width: 280px;
    }

    /* Badge */
    .request-count-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        padding: 0 5px;
        margin-left: 6px;
        font-size: 11px;
        font-weight: 600;
        color: #fff;
        background-color: #FF3B30;
        border-radius: 9px;
    }

    .roommates-container {
        max-width: 1400px;
    }

</style>
